import { toast } from 'react-toastify';
import { getCurrentIdToken, forceRefreshIdToken, handleSessionExpired } from './cognito';

/**
 * Authenticated fetch wrapper.
 *
 * Post-Cognito: ask the SDK for a fresh ID token (which transparently
 * refreshes the access/id tokens when they're stale but the refresh
 * token is still valid, so users stay logged in across the 24-hour
 * token lifetime without a re-MFA prompt).
 *
 * On 401: try ONE forced refresh + retry before signing the user out.
 * Without this, any transient backend hiccup (a JWKS-cache miss on
 * fresh-deployed pods, brief clock skew, etc.) would kick the user
 * back to login and force a fresh MFA prompt every few minutes. The
 * retry is also Promise-deduped at the cognito.js layer so 5 parallel
 * widgets don't trigger 5 parallel refreshes.
 */
async function _doFetch(url, options, token) {
  const headers = { ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return fetch(url, { ...options, headers });
}

export async function apiFetch(url, options = {}) {
  let token = await getCurrentIdToken();

  // null means the session is unrecoverable (refresh token expired, corrupt
  // token cache, etc). handleSessionExpired() clears storage and notifies
  // App.jsx to swap to the Login view in place — no full-page redirect.
  if (!token) {
    handleSessionExpired();
    throw new Error('Unauthorized');
  }

  let response = await _doFetch(url, options, token);

  if (response.status === 401) {
    // The cached token was rejected. Force a refresh-token exchange
    // and retry once.
    const fresh = await forceRefreshIdToken();
    if (fresh && fresh !== token) {
      response = await _doFetch(url, options, fresh);
    }
  }

  if (response.status === 401) {
    handleSessionExpired();
    throw new Error('Unauthorized');
  }

  return response;
}

/**
 * Turn a FastAPI `detail` into something a human can read.
 *
 * `detail` is a STRING for the errors we raise ourselves (HTTPException), but an ARRAY of
 * `{loc, msg, type}` objects for a 422 request-validation failure. Passing that array
 * straight to `toast.error()` renders nothing visible — so a 422 looked exactly like the
 * request having quietly done nothing: the form stayed put, no message, no clue.
 *
 * That is how "clearing a user's email silently fails" presented. The empty string was
 * rejected by `EmailStr` before the handler ran, and the resulting 422 was invisible.
 */
export function detailMessage(detail, fallback = 'Request failed') {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) => {
        if (typeof d === 'string') return d;
        // `loc` is like ["body", "email"] — the last string that isn't the envelope names
        // the field the admin needs to look at.
        const field = Array.isArray(d?.loc)
          ? d.loc.filter((p) => typeof p === 'string' && p !== 'body').pop()
          : null;
        const msg = d?.msg || 'is invalid';
        return field ? `${field}: ${msg}` : msg;
      })
      .filter(Boolean);
    return parts.length ? parts.join('; ') : fallback;
  }
  // `{reason, detail}`: what the MCP routes raise (api/routes/mcpgate.py `_handle`), the
  // operator's text in `detail` and a machine-readable `reason` beside it.
  if (typeof detail === 'object') {
    if (typeof detail.detail === 'string' && detail.detail) return detail.detail;
    return detail.msg || fallback;
  }
  return fallback;
}

/**
 * What the response to an agent action (Start, Stop, Clear history, Delete) says happened:
 * `{ok, body, message}`.
 *
 * `ok` reads the HTTP status AND the body. The server answers a refused or failed action
 * as an HTTP error with a `detail`; before 2026-11-01 it passed the control-plane's own
 * answer on, a 200 carrying `success: false` and an `error`, and a page that read only the
 * status said "done". A page that read only `status: 'success'` said "failed" to every
 * success instead, because the control-plane never sends it.
 *
 * `message` is the server's words: on a failure its reason, or `fallback` when it gave
 * none; on a success its `message` ("Container already running"), or an empty string.
 */
export async function actionOutcome(response, fallback) {
  const body = (await response.json().catch(() => null)) || {};
  const ok = response.ok && body.success !== false;
  const said = ok
    ? body.message
    : detailMessage(body.detail, '') || (typeof body.error === 'string' && body.error)
      || body.message;
  return { ok, body, message: (typeof said === 'string' && said) || (ok ? '' : fallback) };
}

/**
 * One-shot helper around apiFetch for the `mutate-then-toast` pattern that
 * was being re-implemented in every page (try/catch, OK -> toast.success,
 * non-OK -> read err.detail -> toast.error). Returns the parsed JSON body
 * on success, or null on any failure (network, !ok, JSON parse error). The
 * caller can chain on the parsed body, but not on `null`.
 *
 * Usage:
 *   const data = await callApi('/api/coupons', {
 *     method: 'POST',
 *     headers: { 'Content-Type': 'application/json' },
 *     body: JSON.stringify(payload),
 *   }, { successMsg: 'Coupon created', errorMsg: 'Failed to create' });
 *   if (!data) return;
 *
 * Pages that need different toasts per status code, or that need the raw
 * Response (e.g. for explicit 204 vs 200 distinction beyond truthiness),
 * should keep using apiFetch directly.
 */
export async function callApi(url, options = {}, { successMsg, errorMsg } = {}) {
  try {
    const resp = await apiFetch(url, options);
    if (resp.ok) {
      if (successMsg) toast.success(successMsg);
      // 204 No Content has no JSON body — return an empty object so callers
      // can still test for truthiness.
      if (resp.status === 204) return {};
      return await resp.json().catch(() => null);
    }
    const err = await resp.json().catch(() => ({}));
    toast.error(detailMessage(err.detail, errorMsg || 'Request failed'));
    return null;
  } catch {
    toast.error(errorMsg || 'Request failed');
    return null;
  }
}
